(function () {

/*
  FIXES APPLIED IN THIS FILE
  --------------------------
  1. The bonus popup's "Add to cart" button (data-bonus-popup-submit in the
     liquid template) had NO click handler anywhere:
       - BundleForm.bindBonusPopup() only handled close/cancel.
       - setupNormalProductBonus() looked for a different attribute name
         (data-bonus-popup-add) that does not exist in the template, so
         its handler never attached.
     Result: whenever a bonus/free product had variants, the popup opened
     but clicking "Add to cart" did nothing.

     Fix: a single delegated click handler on [data-bonus-popup-submit]
     reads every [data-bonus-variant-select] inside the popup (there can
     be more than one bonus product with variants), validates a variant is
     chosen and available for each, merges that with any no-variant bonus
     items (getFixedBonusLines), and then completes whichever flow opened
     the popup (bundle add-to-cart or normal-product add-to-cart) using a
     small "pending context" stored on the popup element when it was opened.

  2. getFixedBonusLines (previously getPopupBonusLines / part of
     getNormalBonusLines) now reads the hidden input's variant id from
     data-bonus-variant-id as the primary source (falls back to .value).
     The template previously didn't render a `value` attribute on that
     input at all, so these functions always read 0 and silently skipped
     the bonus line. The template has been fixed to also set `value`, and
     this JS is now resilient either way.

  3. Cleaned up dead code (an addEventListener/removeEventListener pair
     that never did anything).
*/

/*
  Which build is live? Printed once in the browser console when a page loads this file,
  and available as window.PI_BUNDLE_BUILD. If the console does NOT show this line, the
  page is running an older pi-bundle.js than this one.
*/
if (!window.PI_BUNDLE_BUILD) {
  window.PI_BUNDLE_BUILD = '2026-10-10b / main title + image stored in PI_MAIN_PRODUCT, used at Add to Cart';
  console.log('[BUNDLE] pi-bundle.js loaded, build ' + window.PI_BUNDLE_BUILD);
}

var MONEY_PATTERN = /{{\s*(\w+)\s*}}/;

function formatWithDelimiters(cents, precision, thousands, decimal) {
  if (isNaN(cents) || cents == null) return '0';

  var value = (cents / 100).toFixed(precision);
  var parts = value.split('.');
  var integer = parts[0];
  var fraction = parts[1] || '';

  integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

  return integer + (precision ? decimal + fraction : '');
}

function formatMoney(cents, format) {
  if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    return Shopify.formatMoney(cents, format);
  }

  format = format || '${{amount}}';

  var value = formatWithDelimiters(cents, 2, ',', '.');

  return format.replace(MONEY_PATTERN, value);
}

function createBundleId() {
  return 'bundle-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
}

// Links a normal product to its free item: both lines get the same timestamp.
// Deliberately NOT a bundle id — a normal product + free item is not a bundle
// and carries no bundle properties.
function createLinkTimestamp() {
  return String(Date.now());
}

function getCartRoot() {
  return document.querySelector('cart-drawer') ||
    document.querySelector('cart-notification') ||
    document;
}

function getRoot(sectionId) {
  if (sectionId) {
    var root = document.getElementById('MainProduct-' + sectionId) ||
      document.querySelector('[data-section-id="' + sectionId + '"]') ||
      document.querySelector('[data-section="' + sectionId + '"]');

    if (root) return root;
  }

  return document;
}

/*
  Names/variants go into the _bundle_items JSON. The cart renders that JSON by
  splitting it on the double-quote character (see bundle-contents-rows.liquid),
  so quotes, backslashes and braces are stripped from the text values here.
*/
function cleanJsonText(value) {
  return String(value || '').replace(/[{}"\\]/g, '').replace(/\s+/g, ' ').trim();
}

function imageUrlAtWidth(url, width) {
  if (!url) return '';

  var sized = String(url).replace(/([?&]width=)\d+/, function (match, prefix) {
    return prefix + width;
  });

  // Shopify can return protocol-relative URLs ("//cdn.shopify.com/...") —
  // make them absolute so they work anywhere they are read later.
  if (sized.indexOf('//') === 0) sized = 'https:' + sized;

  return sized;
}

// 160px wide: the thumbnails in the _bundle_items JSON.
function smallImageUrl(url) {
  return imageUrlAtWidth(url, 160);
}

/*
  The MAIN product's own title and image (never the free product's) — the SAME ones the
  shopper sees at the top of the product page.

  Title, in this order:
    1. The Main product's display name metafield (custom.display_name), exposed as
       data-main-display-name on <bundle-selects>.
    2. The product page heading (<h1 class="pi-title">). It shows the Title block's own
       "heading" setting when one is set, so it can differ from the Shopify product title
       (page: "AMINO ENERGY TWIN PACK", Shopify title: "Optimum Amino Energy Twin Pack + Free
       AE Drink Bottle").
    3. data-main-title / data-bundle-name on <bundle-selects> (the Shopify product title).

  Image: data-main-image on <bundle-selects> (the product's featured image, set in
  pi-bundle-picker.liquid), else the page's og:image meta tag.
*/
function getMainProductTitleAndImage(picker) {
  var scope = (picker && typeof picker.closest === 'function' &&
    (picker.closest('product-info') || picker.closest('.pi-info'))) || document;
  var heading = scope.querySelector('.pi-title') || document.querySelector('.pi-title');
  var headingText = heading ? String(heading.textContent || '').replace(/\s+/g, ' ').trim() : '';

  var displayName = picker && picker.dataset.mainDisplayName
    ? String(picker.dataset.mainDisplayName).replace(/\s+/g, ' ').trim()
    : '';

  var title = displayName || headingText ||
    (picker && (picker.dataset.mainTitle || picker.dataset.bundleName)) || '';

  var image = (picker && picker.dataset.mainImage) ||
    ((document.querySelector('meta[property="og:image"]') || {}).content) || '';

  return { title: title, image: image };
}

/*
  THE VARIABLE. The Main product's title and image are read from the product page ONCE, when
  the picker loads (long before anyone can click Add to Cart), and kept here. ADD TO CART only
  uses what is stored. It is also exposed as window.PI_MAIN_PRODUCT, so you can type
  PI_MAIN_PRODUCT in the browser console and see exactly what will be sent.
*/
var mainProductInfo = { title: '', image: '' };

function rememberMainProduct(picker) {
  var found = getMainProductTitleAndImage(picker);

  // Only fill what was found; never wipe a value that is already stored.
  if (found.title) mainProductInfo.title = found.title;
  if (found.image) mainProductInfo.image = found.image;

  window.PI_MAIN_PRODUCT = mainProductInfo;

  return mainProductInfo;
}

// What Add to Cart uses: the stored values. Only if the page was never read (or only partly),
// read it now as a fallback.
function getStoredMainProduct(picker) {
  if (!mainProductInfo.title || !mainProductInfo.image) rememberMainProduct(picker);

  return mainProductInfo;
}

/*
  Adds _bundle_title (the Main product's title) and _bundle_image (its image, 600px
  wide, full https URL) to a line's properties. A value that cannot be found is left
  out rather than sent empty. Returns the same object so it can be used inline.
*/
function addMainTitleAndImage(properties, picker) {
  var main = getStoredMainProduct(picker);
  var title = String(main.title || '').trim();
  var image = imageUrlAtWidth(main.image, 600);

  if (title) properties._bundle_title = title;
  if (image) properties._bundle_image = image;

  return properties;
}

/*
  The Main product's discount metafield (custom.discount__price), exposed as
  data-discount-percent on <bundle-selects>. Returned exactly as the metafield holds it,
  or '' when it is empty or 0.
*/
function getMainDiscountValue(picker) {
  var value = picker && picker.dataset.discountPercent !== undefined
    ? String(picker.dataset.discountPercent).trim()
    : '';

  return value !== '' && Number(value) !== 0 ? value : '';
}

/*
  What the Main product sends on a line that is NOT a real bundle line (a normal product
  and its free item): the shared _bundle_id, the Main product's discount (_discounts), and
  its title and image (_bundle_title, _bundle_image). Used for the product line and for
  every free line, so they all carry the same values. Returns the same object.
*/
function addMainProductProperties(properties, picker, bundleId, extra) {
  properties._bundle_id = bundleId;

  var discount = getMainDiscountValue(picker);

  if (discount) properties._discounts = discount;

  // The product's own variant id, and the JSON that draws the free item(s) in the cart.
  if (extra && extra.mainVariantId) properties._bundle_main_variant = String(extra.mainVariantId);
  if (extra && extra.itemsJson) properties._bundle_items = extra.itemsJson;

  return addMainTitleAndImage(properties, picker);
}

/*
  The _bundle_items JSON for a normal product + its free item(s): the first row is the product
  itself ("main", its name and image from the stored Main title / image), then one "free" row
  per free item. It goes on the product line AND the free line(s). Because those lines share a
  _bundle_id, the Cart Transform function merges them into one line; without this JSON that
  merged line had nothing to draw, so the free item showed neither nested nor as its own row.
  The cart draws the "free" rows nested under the line (the "main" row is skipped there).
*/
function buildPairItemsJson(picker, bonusLines) {
  var main = getStoredMainProduct(picker);
  var rows = [{
    type: 'main',
    name: cleanJsonText(main.title),
    variant: '',
    image: smallImageUrl(main.image)
  }];

  (bonusLines || []).forEach(function (bonus) {
    if (!bonus || !bonus.id || bonus.available === false) return;

    rows.push({
      type: 'free',
      name: cleanJsonText(bonus.name),
      variant: cleanJsonText(bonus.variantTitle),
      image: smallImageUrl(bonus.image)
    });
  });

  return rows.length > 1 ? JSON.stringify(rows) : '';
}

async function addCartItems(items, cart, activeElement, source) {
  if (!items || !items.length) return;

  var formData = new FormData();

  items.forEach(function (item, index) {
    formData.append('items[' + index + '][id]', item.id);
    formData.append('items[' + index + '][quantity]', item.quantity || 1);

    if (item.properties) {
      Object.keys(item.properties).forEach(function (key) {
        var value = item.properties[key];

        if (value === undefined || value === null) return;

        formData.append('items[' + index + '][properties][' + key + ']', String(value));
      });
    }

    if (item.selling_plan) {
      formData.append('items[' + index + '][selling_plan]', item.selling_plan);
    }
  });

  /*
    Uses `.id` here — confirmed against the real cart-drawer.js source that
    CartDrawer.getSectionsToRender() returns objects with an `.id` field
    only (e.g. { id: 'cart-drawer', selector: '#CartDrawer' }), which is
    also exactly the key renderContents() reads back with
    (parsedState.sections[section.id]). `.section` is checked first only as
    a defensive fallback in case a different cart root (e.g. a theme's
    <cart-notification>) exposes sections under that name instead.
  */
  var sections = 'cart-drawer,cart-icon-bubble';

  if (cart && cart.getSectionsToRender) {
    sections = cart.getSectionsToRender()
      .map(function (section) {
        return section.id || section.section;
      })
      .join(',');
  }

  formData.append('sections', sections);
  formData.append('sections_url', window.location.pathname);

  if (activeElement) {
    activeElement.classList.add('loading');
    activeElement.setAttribute('aria-disabled', 'true');
  }

  try {
    var response = await fetch(
      window.Shopify.routes.root + 'cart/add.js',
      {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      }
    );

    var data = await response.json();

    if (!response.ok) {
      throw new Error(data.description || data.message || 'Unable to add product to cart.');
    }

    /*
      Use <cart-drawer>'s own renderContents(), the theme's normal mechanism
      for this (confirmed against the real cart-drawer.js source). It:
        1. removes the "is-empty" state itself,
        2. reads parsedState.sections['cart-drawer'] / ['cart-icon-bubble']
           (matching cart.getSectionsToRender()'s .id field, which is what
           `sections` was built from above — this was already correct),
        3. calls this.open() itself at the end.

      An earlier version of this file instead published a PUB_SUB_EVENTS
      event and called cart.open() directly, to work around what looked
      like a "drawer stays empty until refresh" bug. That bug's real cause
      was a Liquid error in cart-drawer.liquid at the time (since fixed),
      which made the 'cart-drawer' section render empty/broken — not a
      problem with renderContents() itself. The pub/sub + manual open()
      workaround introduced a second, racing open() call that corrupted the
      transitionend listener cart-drawer.js sets up for focus-trapping,
      crashing trapFocus with a null container. Calling renderContents()
      directly, as Dawn does everywhere else, avoids that entirely.
    */
    /*
      cart-drawer.js's renderContents() only removes the "is-empty" class
      from the nested .drawer__inner element, never from <cart-drawer>
      itself. But open() later checks is-empty on the OUTER <cart-drawer>
      element to decide whether to focus-trap on '.drawer__inner-empty' or
      '#CartDrawer'. If the page first loaded with an empty cart,
      <cart-drawer> keeps its stale "is-empty" class forever after that —
      renderContents() never clears it — so open() looks for
      '.drawer__inner-empty', which no longer exists once the fresh markup
      reflects a non-empty cart, and trapFocus crashes on a null container.

      We just successfully added items, so the cart cannot be empty:
      clear the class ourselves before handing off to renderContents()/open().
    */
    if (cart) {
      cart.classList.remove('is-empty');
    }

    if (cart && typeof cart.renderContents === 'function' && data.sections) {
      cart.renderContents(data);
    } else if (cart && typeof cart.renderContents === 'function') {
      // Sections weren't in the response for some reason (shouldn't happen
      // since `sections` is always requested above) — fetch full cart state
      // as a fallback so renderContents() still has sections to read.
      var cartStateResponse = await fetch(
        window.Shopify.routes.root + 'cart.js',
        { headers: { Accept: 'application/json' } }
      );

      var cartState = await cartStateResponse.json();

      if (cartState) {
        cart.renderContents(cartState);
      }
    } else if (cart && typeof cart.open === 'function') {
      cart.open();
    }

    return data;
  } catch (error) {
    console.error(error);
    throw error;
  } finally {
    if (activeElement) {
      activeElement.classList.remove('loading');
      activeElement.removeAttribute('aria-disabled');
    }
  }
}

/*
  Checks the bundle's selected variants against their stock BEFORE anything
  opens or is sent. A twin bundle uses the same variant in 2 slots, so it
  needs 2 units per bundle; with the bundle quantity that is slots x quantity.
  Returns a message naming the product, or '' when everything is in stock.
  Lines whose stock is unknown (-1: untracked, oversell allowed, or no data)
  are skipped here; Shopify still validates them when the add is sent.
*/
function findBundleStockProblem(lines, quantity) {
  var byVariant = {};
  var order = [];

  (lines || []).forEach(function (line) {
    if (!line || !line.id) return;

    if (!byVariant[line.id]) {
      byVariant[line.id] = { name: line.name || 'This product', stock: line.stock, slots: 0 };
      order.push(line.id);
    }

    byVariant[line.id].slots += 1;
  });

  for (var i = 0; i < order.length; i++) {
    var entry = byVariant[order[i]];
    var needed = entry.slots * (quantity || 1);

    if (typeof entry.stock !== 'number' || entry.stock < 0) continue;

    if (entry.stock === 0) {
      return '"' + entry.name + '" is out of stock. Please change the variant.';
    }

    if (needed > entry.stock) {
      return 'Only ' + entry.stock + ' qty of "' + entry.name + '" is in stock' +
        (needed > 1 ? ', but this bundle needs ' + needed : '') +
        '. Please change the variant.';
    }
  }

  return '';
}

function getVariantIdFromElement(element) {
  if (!element) return 0;

  return Number(
    element.dataset.variantId ||
    element.value ||
    element.getAttribute('value') ||
    0
  );
}

function getSelectedVariantId(select) {
  if (!select) return 0;

  return Number(select.value || select.dataset.variantId || 0);
}

function getBundleQuantity(form) {
  var quantityInput = form && form.querySelector('[name="quantity"],input.quantity__input');
  var quantity = quantityInput ? Number(quantityInput.value) : 1;

  if (!quantity || quantity < 1) quantity = 1;

  return quantity;
}

/*
  No-variant bonus/free products are rendered as hidden
  <input data-bonus-fixed data-bonus-variant-id="..." value="...">.
  Reads data-bonus-variant-id first (always present), falls back to
  .value for older markup that only sets the value attribute.
*/
function getFixedBonusLines(root) {
  var lines = [];

  if (!root) return lines;

  root.querySelectorAll('[data-bonus-fixed]').forEach(function (input) {
    var id = Number(input.dataset.bonusVariantId || input.value || 0);

    if (!id) return;

    lines.push({
      id: id,
      quantity: 1,
      available: input.dataset.bonusAvailable !== 'false',
      name: input.dataset.bonusName || '',
      variantTitle: input.dataset.bonusVariantTitle || '',
      image: input.dataset.bonusImage || ''
    });
  });

  return lines;
}

/*
  Reads every variant <select> inside the bonus popup (there can be more
  than one bonus product with variants shown at once) and returns the
  currently chosen variant for each. Returns null if any select doesn't
  have a valid, available variant chosen, so the caller can show an
  error instead of silently adding an incomplete set.
*/
function getPopupSelectedVariantLines(popup) {
  if (!popup) return [];

  var selects = popup.querySelectorAll('[data-bonus-variant-select]');
  var lines = [];
  var hasInvalid = false;

  selects.forEach(function (select) {
    var id = Number(select.value || 0);
    var option = select.options[select.selectedIndex];
    var available = option ? option.dataset.available !== 'false' : true;

    if (!id || !available) {
      hasInvalid = true;
      return;
    }

    var productWrap = select.closest('[data-bonus-product]');

    lines.push({
      id: id,
      quantity: 1,
      available: true,
      name: productWrap ? (productWrap.dataset.bonusName || '') : '',
      variantTitle: option ? (option.dataset.variantTitle || option.textContent.trim()) : '',
      image: option ? (option.dataset.variantImage || '') : ''
    });
  });

  if (hasInvalid) return null;

  return lines;
}

function showBonusPopupError(popup, message) {
  var errorElement = popup && popup.querySelector('[data-bonus-popup-error]');

  if (!errorElement) return;

  errorElement.textContent = message;
  errorElement.removeAttribute('hidden');
}

function clearBonusPopupError(popup) {
  var errorElement = popup && popup.querySelector('[data-bonus-popup-error]');

  if (!errorElement) return;

  errorElement.textContent = '';
  errorElement.setAttribute('hidden', '');
}

function closeBonusPopupElement(popup) {
  if (!popup) return;

  /*
    Blur any focused element inside the popup BEFORE hiding/aria-hiding it.
    Setting aria-hidden="true" on a container that still holds focus is
    invalid per the ARIA spec (and Chrome warns/blocks it) — a hidden
    element cannot also be the one receiving keyboard input.
  */
  if (popup.contains(document.activeElement)) {
    document.activeElement.blur();
  }

  popup.classList.remove('is-open');
  popup.setAttribute('hidden', '');
  popup.setAttribute('aria-hidden', 'true');
  popup._pendingBundleSubmit = null;
  popup._pendingNormalSubmit = null;
  document.documentElement.classList.remove('bonus-popup-open');
}

function openBonusPopupElement(popup) {
  if (!popup) return;

  clearBonusPopupError(popup);
  popup.classList.add('is-open');
  popup.removeAttribute('hidden');
  // Must come off before anything inside the popup can receive focus —
  // see the note in closeBonusPopupElement() above for why.
  popup.setAttribute('aria-hidden', 'false');
  document.documentElement.classList.add('bonus-popup-open');
}

if (!customElements.get('bundle-selects')) {
  customElements.define(
    'bundle-selects',
    class BundleSelects extends HTMLElement {
    constructor() {
      super();

      /*
        FIX: was reading this.dataset.sectionId, but the actual attribute
        rendered in pi-bundle-picker.liquid is data-section (not
        data-section-id) — so this.sectionId was always an empty string.
        Checked both so it's resilient either way.
      */
      this.sectionId = this.dataset.section || this.dataset.sectionId || '';
      this.moneyFormat = this.dataset.moneyFormat || '';
      this.isBundle = this.dataset.isBundle === 'true';
      this.hasBonus = this.dataset.hasBonus === 'true';
      this.form = null;
    }

    connectedCallback() {
      this.form = this.closest('bundle-form')?.querySelector('form') ||
        this.closest('product-info')?.querySelector('form');

      // Store the Main product's title and image now, ready for Add to Cart.
      rememberMainProduct(this);
      console.log('[BUNDLE] main product stored for Add to Cart:', mainProductInfo);

      this.bindVariantChanges();
      this.bindBonusPopupTrigger();
      this.bindStackDetailsLink();
      this.watchBundlePriceElement();
      this.updateBundlePrice();
    }

    /*
      "click here" inside the out-of-stock note scrolls down to the Stack
      Details section (bundle-details.liquid), where each product in the
      bundle is listed on its own and can be bought individually. The link is
      rendered with href="#" so it still reads as a link with JS off; the
      scroll itself is done here, and if that section isn't on the page
      (bundle_products metafield empty) nothing happens rather than jumping
      the page to the top.
    */
    bindStackDetailsLink() {
      var noteElement = this.querySelector('[data-bundle-note]');

      if (!noteElement) return;

      // No Stack Details section to scroll to: drop the action rather than
      // leave a button that does nothing. The note itself still shows.
      if (!document.querySelector('.bundle-details-wrapper')) {
        var deadLink = noteElement.querySelector('[data-bundle-stack-link]');
        if (deadLink) deadLink.hidden = true;
        return;
      }

      noteElement.addEventListener('click', function (event) {
        var link = event.target.closest('[data-bundle-stack-link]');

        if (!link) return;

        event.preventDefault();

        var stackSection = document.querySelector('.bundle-details-wrapper');

        if (!stackSection) return;

        var reduceMotion = window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        stackSection.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      });
    }

    /*
      Defensive fix: on some themes, a global script (commonly
      product-info.js) also listens for `change` on any <select> inside the
      product form and reacts to it as if it were a real variant change —
      re-fetching/re-rendering the price area from the server, which
      overwrites the discount-adjusted value updateBundlePrice() just wrote,
      right after it wrote it. Rather than needing to track down and patch
      that other script, this watches the price node itself and re-applies
      our own value whenever something else changes it out from under us.
    */
    watchBundlePriceElement() {
      var priceElement = this.querySelector('[data-bundle-price]');

      if (!priceElement || typeof MutationObserver === 'undefined') return;

      var self = this;
      var observerConfig = { childList: true, characterData: true, subtree: true };

      var observer = new MutationObserver(function () {
        // Disconnect before writing, otherwise our own update below queues
        // another mutation record that would re-trigger this same callback
        // on the next microtask — MutationObserver batches asynchronously,
        // so a same-tick boolean flag doesn't prevent that.
        observer.disconnect();
        self.updateBundlePrice();
        observer.observe(priceElement, observerConfig);
      });

      observer.observe(priceElement, observerConfig);

      this.bundlePriceObserver = observer;
    }

    disconnectedCallback() {
      if (this.bundlePriceObserver) {
        this.bundlePriceObserver.disconnect();
      }
    }

    bindVariantChanges() {
      this.addEventListener('change', () => {
        this.updateBundlePrice();

        // A previous add-to-cart error (e.g. "...is already sold out.") is
        // stale as soon as the shopper picks something different.
        var bundleForm = (this.closest('product-info') || document).querySelector('bundle-form');

        if (bundleForm && typeof bundleForm.showError === 'function') {
          bundleForm.showError('');
        }
      });
    }

    /*
      Main product's own gallery image is intentionally left alone here —
      it never changes based on a sub-product's variant selection. Only
      each sub-product row's own small thumbnail (in updateBundlePrice()
      below) updates to reflect its own selected variant.
    */

    getChildCards() {
      return Array.from(this.querySelectorAll('[data-bundle-slot]'));
    }

    getCardVariantId(card) {
      if (!card) return 0;

      var select = card.querySelector('[data-bundle-variant-select]') ||
        card.querySelector('select[name^="options"]') ||
        card.querySelector('select');

      return getSelectedVariantId(select);
    }

    getSelectedChildLines() {
      var lines = [];

      this.getChildCards().forEach(function (card) {
        var fixedElement = card.querySelector('[data-bundle-variant-id]');
        var variantId = getVariantIdFromElement(fixedElement);

        if (!variantId) {
          variantId = this.getCardVariantId(card);
        }

        if (!variantId) return;

        var titleElement = card.querySelector('.pi-variants__group-title');
        var select = card.querySelector('[data-bundle-variant-select]');
        var option = select && select.options[select.selectedIndex];
        var rowImage = card.querySelector('.pi-variants__group-media img');
        var variantTitle = '';
        var image = '';
        var stockSource = '';

        if (option) {
          variantTitle = option.dataset.variantTitle || option.textContent.trim();
          image = option.dataset.variantImage || '';
          stockSource = option.dataset.stock;
        } else if (fixedElement) {
          variantTitle = fixedElement.textContent.trim();
          stockSource = fixedElement.dataset.stock;
        }

        // -1 = stock isn't tracked / can be oversold, or the attribute is missing.
        var stock = Number(stockSource);

        if (stockSource === undefined || stockSource === '' || isNaN(stock)) stock = -1;

        if (!image && rowImage) image = rowImage.getAttribute('src') || '';

        lines.push({
          id: variantId,
          quantity: 1,
          name: titleElement ? titleElement.textContent.trim() : '',
          variantTitle: variantTitle,
          image: image,
          stock: stock
        });
      }, this);

      return lines;
    }

    updateBundlePrice() {
      var cards = this.getChildCards();

      if (!cards.length) return;

      var total = 0;
      var compareTotal = 0;
      var allAvailable = true;
      var missingTitles = [];

      cards.forEach(function (card) {
        var price = 0;
        var comparePrice = 0;
        var variantSelect = card.querySelector('[data-bundle-variant-select]') || card.querySelector('select');
        var fixedElement = card.querySelector('[data-bundle-fixed]');
        var isAvailable = true;
        var variantImage = '';

        if (variantSelect) {
          var option = variantSelect.options[variantSelect.selectedIndex];

          if (option) {
            price = Number(option.dataset.price || 0);
            comparePrice = Number(option.dataset.comparePrice || 0);
            isAvailable = option.dataset.available !== 'false';
            variantImage = option.dataset.variantImage || '';
          }
        } else if (fixedElement) {
          // Single-variant sub-product: no dropdown, so its price only ever
          // comes from this element's own data attributes (previously
          // missing entirely — fixed children always contributed $0).
          price = Number(fixedElement.dataset.price || 0);
          comparePrice = Number(fixedElement.dataset.comparePrice || 0);
        } else {
          // Fallback for any other markup shape.
          price = Number(card.dataset.variantPrice || card.dataset.price || 0);
        }

        if (!comparePrice || comparePrice <= price) {
          comparePrice = price;
        }

        if (!isAvailable) {
          allAvailable = false;

          var titleElement = card.querySelector('.pi-variants__group-title');

          if (titleElement) {
            missingTitles.push(titleElement.textContent.trim());
          }
        }

        // Ported from the old working pi-bundle.js: marks the row so CSS can
        // show its "Sold out" badge (see .pi-bundle__badge in
        // pi-bundle-picker.liquid) when the currently selected variant isn't
        // available.
        card.classList.toggle('pi-bundle__item--unavailable', !isAvailable);

        // Ported from the old working pi-bundle.js (there: paintChild()):
        // each row's own small thumbnail — not the main product gallery —
        // updates to the selected variant's photo.
        if (variantImage) {
          var rowImage = card.querySelector('.pi-variants__group-media img');

          if (rowImage && rowImage.src !== variantImage) {
            rowImage.removeAttribute('srcset');
            rowImage.src = variantImage;
          }
        }

        total += price;
        compareTotal += comparePrice;
      });

      /*
        Same formula as the cart drawer/cart page (see cart-drawer-row.liquid /
        cart-page-row.liquid): displayed price is the sub-products' total
        minus this percentage, read from custom.discount__price via
        data-discount-percent on this element. Re-applied on every child
        variant change so the PDP price never drifts from what the cart
        will show once added. The discount is NOT applied to compareTotal —
        that's the "was" price shown struck through, same as the original
        SSR bundle_compare calculation in main-product.liquid.
      */
      var discountPercent = Number(this.dataset.discountPercent || 0);
      var displayTotal = total;

      if (discountPercent > 0) {
        var discountAmount = Math.round((total * discountPercent) / 100);
        displayTotal = total - discountAmount;
      }

      /*
        THE ACTUAL BUG THIS FIXES: [data-bundle-price] and
        [data-bundle-instalment] live in the "price" block, a SIBLING of
        <bundle-selects> (both sit inside <product-info id="MainProduct-...">
        but neither contains the other) — see main-product.liquid, where
        'price' and 'variant_picker' are separate entries in the same
        blocks loop. `this.querySelector(...)` only searches within
        <bundle-selects>'s own subtree, so it always silently returned null
        here — no error, just nothing to update. Searching from the whole
        product-info root (matching the old working file's getRoot()
        pattern) finds it correctly.
      */
      var root = getRoot(this.sectionId);
      var priceElement = root.querySelector('[data-bundle-price]');

      if (priceElement) {
        priceElement.innerHTML = formatMoney(displayTotal, this.moneyFormat);
      }

      var instalmentElement = root.querySelector('[data-bundle-instalment]');

      // Rebuilt from the block's own text and payment count — writing the total straight in
      // dropped the "or 4 payments of … with" wording and showed the full price as one payment.
      if (instalmentElement) {
        var instalmentCount = parseInt(instalmentElement.getAttribute('data-instalment-count'), 10) || 4;
        var instalmentText = instalmentElement.getAttribute('data-instalment-text') || '[price]';
        var instalmentAmount = formatMoney(Math.round(displayTotal / instalmentCount), this.moneyFormat);

        instalmentElement.innerHTML = instalmentText.replace('[price]', instalmentAmount);
      }

      /*
        Compare-at (struck-through "was" price) — same root-vs-this scoping
        bug as above applied here too, plus this element previously had no
        live-update logic at all. Shown only when the combined compare-at
        total is actually higher than what's being charged; hidden and
        toggled off (with the on-sale class) otherwise, since a variant swap
        can turn a discounted combination into a non-discounted one or the
        reverse.
      */
      var comparePriceElement = root.querySelector('[data-bundle-compare-price]');
      var priceWrapper = root.querySelector('[data-bundle-price-wrapper]');
      var isOnSale = compareTotal > displayTotal;

      if (comparePriceElement) {
        comparePriceElement.hidden = !isOnSale;

        var compareAmountElement = comparePriceElement.querySelector('[data-bundle-compare-amount]');

        if (compareAmountElement) {
          compareAmountElement.innerHTML = formatMoney(compareTotal, this.moneyFormat);
        }
      }

      if (priceWrapper) {
        priceWrapper.classList.toggle('pi-price--on-sale', isOnSale);
      }

      // Ported from the old working pi-bundle.js (there: updateNote()): the
      // [data-bundle-note] element already exists in pi-bundle-picker.liquid
      // but nothing was ever writing to it. The copy itself is rendered in
      // the snippet (it contains the "click here" link), so all that happens
      // here is showing it whenever any selected sub-product is unavailable.
      var noteElement = this.querySelector('[data-bundle-note]');

      if (noteElement) {
        noteElement.hidden = !missingTitles.length;
      }

      // Ported from the old working pi-bundle.js (there: setBuyable()): the
      // Add to Cart button disables itself and shows the sold-out label when
      // any selected sub-product combination isn't actually buyable.
      var bundleFormElement = this.closest('bundle-form');
      var submitButton = this.form && this.form.querySelector('[type="submit"]');

      if (submitButton) {
        submitButton.disabled = !allAvailable;
        submitButton.setAttribute('aria-disabled', allAvailable ? 'false' : 'true');

        var submitLabel = submitButton.querySelector('span');

        if (submitLabel && bundleFormElement) {
          if (!allAvailable && bundleFormElement.dataset.soldOutLabel) {
            submitLabel.textContent = bundleFormElement.dataset.soldOutLabel;
          } else if (allAvailable && bundleFormElement.dataset.addLabel) {
            submitLabel.textContent = bundleFormElement.dataset.addLabel;
          }
        }
      }
    }

    /*
      Only responsible for opening the popup when a [data-bonus-popup-trigger]
      is clicked directly (e.g. "choose your gift" link). Submitting the
      bundle FORM when a bonus choice is still required is handled by
      BundleForm.onSubmit, and the popup's own submit button is handled by
      the single global handler set up in setupBonusPopupSubmit().
    */
    bindBonusPopupTrigger() {
      var root = this;

      document.addEventListener('click', function (event) {
        var button = event.target.closest('[data-bonus-popup-trigger]');

        if (!button) return;
        if (!root.contains(button)) return;

        event.preventDefault();

        var popup = document.querySelector('[data-bonus-popup]');

        if (!popup) return;

        var bonusId = button.dataset.bonusId || button.dataset.variantId || '';
        var select = popup.querySelector('[data-bonus-variant-select]');

        if (select && bonusId) {
          select.value = bonusId;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }

        openBonusPopupElement(popup);
      });
    }
  }
);
}
if (!customElements.get('bundle-form')) {
  customElements.define(
    'bundle-form',
    class BundleForm extends HTMLElement {
    constructor() {
      super();

      this.form = null;
      this.picker = null;
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.picker = this.querySelector('bundle-selects') ||
        this.closest('product-info')?.querySelector('bundle-selects');

      if (!this.form) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    setLoading(state) {
      var button = this.form?.querySelector('[type="submit"]');

      if (!button) return;

      if (state) {
        button.classList.add('loading');
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
      }
    }

    /*
      PDP error (shown above the Add to Cart button). The error box
      (.product-form__error-message-wrapper) is rendered by pi-buy-row.liquid
      BEFORE the <form>, as a sibling inside <bundle-form> — so it must be
      looked up from this element, not from this.form, and the wrapper itself
      (which carries `hidden`) has to be un-hidden, not just the inner span.
      Pass an empty message to hide it again.
    */
    showError(message) {
      var wrapper = this.querySelector('.product-form__error-message-wrapper');
      var errorElement = this.querySelector('.product-form__error-message');

      if (!errorElement) return;

      errorElement.textContent = message || '';

      if (message) {
        errorElement.removeAttribute('hidden');
        if (wrapper) wrapper.removeAttribute('hidden');
      } else if (wrapper) {
        wrapper.setAttribute('hidden', '');
      }
    }

    async onSubmit(event) {
      event.preventDefault();

      var picker = this.picker;

      if (!picker) {
        console.error('Bundle picker not found.');
        return;
      }

      var quantity = getBundleQuantity(this.form);
      var scopeRoot = this.closest('product-info') || this.closest('section') || document;
      var fixedBonusLines = getFixedBonusLines(scopeRoot);
      var lines = picker.getSelectedChildLines();

      /*
        Not enough stock: say so on the PDP, with the product name, BEFORE the
        free-gift popup would open (and before anything is sent). Nothing opens
        again until the shopper changes a variant — that clears this message
        (see BundleSelects.bindVariantChanges) — and clicks Add to Cart again.
      */
      var stockProblem = findBundleStockProblem(lines, quantity);

      if (stockProblem) {
        this.showError(stockProblem);
        return;
      }

      /*
        If this bundle has bonus products WITH variants, the shopper must
        choose a variant for each one before we can add anything. Open the
        popup and stash everything onSubmit already computed (lines,
        quantity) on the popup element so the popup's own submit button
        (handled globally below) can finish the job once a variant is
        picked — instead of the previous code, which opened the popup and
        then had nowhere for the click to go.
      */
      if (picker.hasBonus) {
        var popup = document.querySelector('[data-bonus-popup]');

        if (popup && popup.querySelector('[data-bonus-variant-select]')) {
          popup._pendingBundleSubmit = {
            bundleForm: this,
            lines: lines,
            fixedBonusLines: fixedBonusLines,
            quantity: quantity
          };

          openBonusPopupElement(popup);
          return;
        }
      }

      this.showError('');
      this.setLoading(true);

      try {
        await this.addBundleToCart(lines, fixedBonusLines, quantity);
      } catch (error) {
        // No free-gift popup involved here, so the error goes on the PDP,
        // above the Add to Cart button.
        console.error('Bundle add to cart error:', error);
        this.showError(error.message || 'Unable to add bundle.');
      } finally {
        this.setLoading(false);
      }
    }

    async addBundleToCart(lines, bonusLines, quantity) {
      var bundleId = createBundleId();
      var picker = this.picker;
      var items = [];

      /*
        MAIN PRODUCT VARIANT

        A real bundle no longer adds its Main product to the cart: only the
        bundle products (and the free item) are added. The main variant is
        read here only for the "free item only" case below, where the product
        itself is the thing being bought.

        Read from data-main-variant-id on the <bundle-selects> element (set
        from product.selected_or_first_available_variant.id in the liquid
        template). Fallback: normal product form variant input.
      */
      var mainVariantId = Number(
        picker?.dataset.mainVariantId ||
        this.form?.dataset.mainVariantId ||
        this.form?.querySelector('[name="id"]')?.value ||
        0
      );

      /*
        No bundle child products selected — only a free item is being added.
        That is not a bundle, so it is NOT made into one: the product and its free item go to the
        cart as two separate lines, with no _bundle_id, _bundle_items, _bundle_title, _bundle_image
        or _discounts. The only things that tie them together are a _timestamp shared by both
        lines (so removing one removes the other) and the free line's _bonus: true.
      */
      if (!lines.length) {
        if (!mainVariantId) {
          throw new Error('Product variant not found.');
        }

        var freeOnlyTimestamp = createLinkTimestamp();
        var freeOnlyItems = [{
          id: mainVariantId,
          quantity: quantity,
          properties: { _timestamp: freeOnlyTimestamp }
        }];

        bonusLines.forEach(function (bonus) {
          if (!bonus || !bonus.id) return;
          if (bonus.available === false) return;

          freeOnlyItems.push({
            id: bonus.id,
            quantity: quantity,
            properties: { _bonus: true, _timestamp: freeOnlyTimestamp }
          });
        });

        await addCartItems(
          freeOnlyItems,
          getCartRoot(),
          this.form.querySelector('[type="submit"]'),
          'bundle'
        );

        return;
      }

      // Name / image / variant name of every bundle item and free item, as a JSON
      // string ("_" prefix keeps it hidden). There is no Main line any more, so it
      // travels on each bundle product line below.
      var summaryItems = [];

      // The bundle (Main) product itself comes FIRST in the JSON: its title and
      // image. data-main-title / data-main-image come from pi-bundle-picker.liquid;
      // if those attributes aren't there yet, fall back to the page's og:image
      // meta tag and the product <h1> so it still works.
      var mainSource = getStoredMainProduct(picker);
      var mainImageSource = mainSource.image;
      var mainTitleSource = mainSource.title;

      summaryItems.push({
        type: 'main',
        name: cleanJsonText(mainTitleSource),
        variant: '',
        image: smallImageUrl(mainImageSource)
      });

      lines.forEach(function (variant) {
        if (!variant || !variant.id) return;
        summaryItems.push({
          type: 'item',
          name: cleanJsonText(variant.name),
          variant: cleanJsonText(variant.variantTitle),
          image: smallImageUrl(variant.image)
        });
      });

      bonusLines.forEach(function (bonus) {
        if (!bonus || !bonus.id || bonus.available === false) return;
        summaryItems.push({
          type: 'free',
          name: cleanJsonText(bonus.name),
          variant: cleanJsonText(bonus.variantTitle),
          image: smallImageUrl(bonus.image)
        });
      });

      var bundleItemsJson = summaryItems.length ? JSON.stringify(summaryItems) : '';

      /*
        The bundle's own (Main product) title and image, sent on every bundle
        product as _bundle_title / _bundle_image — the title and picture the
        bundle should be shown with. The image is 600px wide (the JSON uses 160px
        thumbnails) and always a full https URL.
      */
      var bundleTitle = String(mainTitleSource || '').trim();
      var bundleImage = imageUrlAtWidth(mainImageSource, 600);

      /*
        Bundle discount: the Main product's discount metafield (custom.discount__price,
        exposed as data-discount-percent on <bundle-selects>) goes on every bundle
        product line (and on the free product) as _discounts (the attribute the Cart
        Transform function reads), since there is no Main line to carry it.
        Main 10 -> each bundle product, and the free product, gets 10.
        The value is sent exactly as the metafield holds it; it is left out only when
        it is empty or 0.
      */
      var bundleDiscounts = getMainDiscountValue(picker);

      /*
        BUNDLE CHILD PRODUCTS and FREE / BONUS PRODUCTS

        Both are built by the SAME function below, so the free product carries
        exactly the properties a bundle product does: _bundle_id, _bundle_child,
        _bundle_slot, _discounts, _bundle_title, _bundle_image and the
        _bundle_items JSON. The only thing that sets the free product apart is
        _bonus: true — kept so it is still recognisable as the free item (free
        pricing, the "Free" label). The _bundle_items JSON also marks it with
        "type":"free".

        Each line gets its own _bundle_slot (1, 2, 3 … counting the free product
        after the bundle products). Shopify merges cart items that have the same
        variant AND the same properties into ONE line with a higher quantity —
        which is exactly what a twin bundle (the same variant in 2 slots) would
        otherwise turn into. The slot number makes every line's properties
        different, so each one stays its own line.
      */
      var bundleSlot = 0;

      function bundleLineProperties() {
        bundleSlot += 1;

        var properties = {
          _bundle_id: bundleId,
          _bundle_child: true,
          _bundle_slot: bundleSlot
        };

        // The Main product's own variant. The Cart Transform function can use it as the
        // variant the merged bundle line is built on, so that line belongs to the bundle's
        // product (its name, link, image) instead of to one of the bundle's products or
        // to the free item.
        if (mainVariantId) {
          properties._bundle_main_variant = String(mainVariantId);
        }

        if (bundleDiscounts) {
          properties._discounts = bundleDiscounts;
        }

        if (bundleTitle) {
          properties._bundle_title = bundleTitle;
        }

        if (bundleImage) {
          properties._bundle_image = bundleImage;
        }

        if (bundleItemsJson) {
          properties._bundle_items = bundleItemsJson;
        }

        return properties;
      }

      lines.forEach(function (variant) {
        if (!variant || !variant.id) return;

        items.push({
          id: variant.id,
          quantity: quantity,
          properties: bundleLineProperties()
        });
      });

      // Every bonus line (a no-variant hidden input, or a variant picked in the popup).
      bonusLines.forEach(function (bonus) {
        if (!bonus || !bonus.id) return;
        if (bonus.available === false) return;

        var properties = bundleLineProperties();

        properties._bonus = true;

        items.push({
          id: bonus.id,
          quantity: quantity,
          properties: properties
        });
      });

      var cart = getCartRoot();

      // CONSOLE — exactly what is sent to Shopify (one entry per cart item).
      console.log('[BUNDLE] sending ' + items.length + ' item(s) to /cart/add.js:', items.map(function (line) {
        return { variant_id: line.id, quantity: line.quantity, properties: line.properties };
      }));

      var addResponse = await addCartItems(
        items,
        cart,
        this.form.querySelector('[type="submit"]'),
        'bundle'
      );

      // CONSOLE — what Shopify actually added. Identical items (same variant AND
      // same properties) are merged by Shopify into ONE line with a higher quantity.
      if (addResponse && Array.isArray(addResponse.items)) {
        console.log('[BUNDLE] Shopify added ' + addResponse.items.length + ' line(s):', addResponse.items.map(function (line) {
          return { variant_id: line.variant_id, quantity: line.quantity, title: line.title };
        }));
      }

      // CONSOLE — what the cart really holds afterwards (after the Cart Transform
      // function has run). has_components: true marks a merged bundle line.
      try {
        var cartAfterResponse = await fetch(window.Shopify.routes.root + 'cart.js', {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        var cartAfter = await cartAfterResponse.json();

        console.log('[BUNDLE] cart now has ' + cartAfter.items.length + ' line(s):', cartAfter.items.map(function (line) {
          return {
            title: line.title,
            quantity: line.quantity,
            price: line.price,
            has_components: line.has_components,
            _bundle_id: line.properties && line.properties._bundle_id
          };
        }));
      } catch (logError) {
        console.warn('[BUNDLE] could not read the cart for the log:', logError);
      }
    }
  }
);
}
function getFormProperties(form) {
  var properties = {};

  if (!form) return properties;

  form.querySelectorAll('[name^="properties["]').forEach(function (input) {
    var match = input.name.match(/^properties\[(.+)\]$/);

    if (!match) return;

    if (input.type === 'checkbox' || input.type === 'radio') {
      if (!input.checked) return;
    }

    properties[match[1]] = input.value;
  });

  return properties;
}

/*
  Normal (non-bundle) product + free bonus. This is NOT a bundle, so nothing here makes one: the
  product and every free item are added as separate cart lines, with no _bundle_id,
  _bundle_items, _bundle_main_variant, _bundle_title, _bundle_image or _discounts.
  What links them is only:
    - a _timestamp shared by the product line and the free line(s), so removing one removes the
      group (see the cart rows), and
    - _bonus: true on each free line, so it is drawn as "Free item".
  The product line keeps whatever properties its own form already sends.
*/
function buildNormalProductItems(form, picker, bonusLines) {
  var items = [];

  var mainVariantInput = form.querySelector('[name="id"]');
  var mainVariantId = Number(mainVariantInput?.value || 0);

  if (!mainVariantId) {
    throw new Error('Product variant not found.');
  }

  var quantityInput = form.querySelector('[name="quantity"]');
  var quantity = Number(quantityInput?.value || 1);

  if (!quantity || quantity < 1) {
    quantity = 1;
  }

  var properties = getFormProperties(form);
  var linkTimestamp = createLinkTimestamp();

  properties._timestamp = linkTimestamp;

  items.push({
    id: mainVariantId,
    quantity: quantity,
    properties: properties
  });

  bonusLines.forEach(function (bonus) {
    if (!bonus || !bonus.id) return;
    if (bonus.available === false) return;

    items.push({
      id: bonus.id,
      quantity: quantity,
      properties: {
        _bonus: true,
        _timestamp: linkTimestamp
      }
    });
  });

  return items;
}

function findNormalProductPicker(form) {
  if (!form) return null;

  return form.closest('product-info')?.querySelector('bundle-selects') ||
    form.closest('section')?.querySelector('bundle-selects') ||
    document.querySelector('bundle-selects');
}

/*
  Variant-conditional free item (see data-any-variant-free-items and
  data-variant-map's "free_items" field, both set in main-product.liquid
  from the variant metafield custom.free_items):

  - If NO variant of this product has free_items set to true anywhere,
    the bonus is offered unconditionally — this is the original, default
    behavior for products that don't use this feature at all.
  - If AT LEAST ONE variant has it set to true, the bonus is only offered
    when the shopper's CURRENTLY SELECTED variant is one of those —
    every other variant of that same product behaves like a normal
    product with no bonus.
*/
function getCurrentMainVariantId(form) {
  var idInput = form && form.querySelector('[name="id"]');

  return Number(idInput?.value || 0);
}

function getVariantMap(root) {
  var script = root.querySelector('[data-variant-map]');

  if (!script) return null;

  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    return null;
  }
}

function shouldOfferBonusForCurrentVariant(root, form) {
  var wrapper = root.querySelector('[data-any-variant-free-items]');
  var anyVariantHasFreeItems = wrapper && wrapper.dataset.anyVariantFreeItems === 'true';

  if (!anyVariantHasFreeItems) {
    // No variant anywhere has the flag — unconditional, same as before.
    return true;
  }

  var variantId = getCurrentMainVariantId(form);
  var variantMap = getVariantMap(root);

  if (variantMap) {
    var matchedVariant = variantMap.find(function (variant) {
      return Number(variant.id) === variantId;
    });

    return !!(matchedVariant && (matchedVariant.free_items === true || matchedVariant.free_items === 'true'));
  }

  // No variant-map JSON found — this only happens for a single-variant
  // product, where that one variant is always the selected one. If
  // anyVariantHasFreeItems is true here, it can only be that single
  // variant, so it's always the active selection.
  return true;
}

function setupNormalProductBonus() {
  document.addEventListener('click', async function (event) {
    var submit = event.target.closest('form[action*="/cart/add"] [type="submit"]');

    if (!submit) return;

    var form = submit.closest('form');

    if (!form) return;
    if (form.closest('bundle-form') || form.closest('bundle-selects')) return;

    var root = form.closest('product-info') || form.closest('section') || document;
    var picker = findNormalProductPicker(form);

    var hasBonus = root.querySelector('[data-bonus-popup]') ||
      root.querySelector('[data-bonus-fixed]');

    if (!hasBonus) return;
    if (!shouldOfferBonusForCurrentVariant(root, form)) return;

    event.preventDefault();
    event.stopPropagation();

    var popup = root.querySelector('[data-bonus-popup]') || document.querySelector('[data-bonus-popup]');
    var fixedBonusLines = getFixedBonusLines(root);

    if (popup && popup.querySelector('[data-bonus-variant-select]')) {
      /*
        Bonus has variants: open the popup and stash what's needed so the
        global [data-bonus-popup-submit] handler below can finish the add
        once the shopper picks a variant.
      */
      popup._pendingNormalSubmit = {
        form: form,
        picker: picker,
        submitEl: submit,
        fixedBonusLines: fixedBonusLines
      };

      openBonusPopupElement(popup);
      return;
    }

    // No-variant bonus only: add everything immediately.
    submit.classList.add('loading');
    submit.setAttribute('aria-disabled', 'true');

    try {
      var items = buildNormalProductItems(form, picker, fixedBonusLines);
      var cart = getCartRoot();

      await addCartItems(items, cart, submit, 'normal');
    } catch (error) {
      console.error('Normal product add to cart error:', error);
    } finally {
      submit.classList.remove('loading');
      submit.removeAttribute('aria-disabled');
    }
  }, true);
}

/*
  Single, global handler for the bonus popup's "Add to cart" button
  (data-bonus-popup-submit). Works for both flows that can open the
  popup — the bundle form (BundleForm.onSubmit) and a normal product form
  (setupNormalProductBonus) — by reading whichever pending context was
  stashed on the popup element when it was opened.
*/
function setupBonusPopupSubmit() {
  document.addEventListener('click', async function (event) {
    var button = event.target.closest('[data-bonus-popup-submit]');

    if (!button) return;

    var popup = button.closest('[data-bonus-popup]');

    if (!popup) return;

    event.preventDefault();

    var pickedLines = getPopupSelectedVariantLines(popup);

    if (pickedLines === null) {
      showBonusPopupError(popup, 'Please select an available option for each free gift.');
      return;
    }

    clearBonusPopupError(popup);

    // Dawn's spinner ships with .hidden (display: none !important), so it
    // has to be un-hidden alongside .loading or the button just goes blank.
    var spinner = button.querySelector('.loading__spinner');

    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-busy', 'true');
    if (spinner) spinner.classList.remove('hidden');

    try {
      if (popup._pendingBundleSubmit) {
        var bundleContext = popup._pendingBundleSubmit;
        var allBonusLines = bundleContext.fixedBonusLines.concat(pickedLines);

        bundleContext.bundleForm.showError('');
        bundleContext.bundleForm.setLoading(true);

        try {
          await bundleContext.bundleForm.addBundleToCart(
            bundleContext.lines,
            allBonusLines,
            bundleContext.quantity
          );

          closeBonusPopupElement(popup);
        } catch (error) {
          // The free-gift popup is open, so the error is shown there (it
          // stays open so the shopper can read it) rather than on the PDP
          // hidden behind the overlay. e.g. "The product 'X' is already sold out."
          console.error('Bundle add to cart error:', error);
          showBonusPopupError(popup, error.message || 'Unable to add bundle.');
        } finally {
          bundleContext.bundleForm.setLoading(false);
        }
      } else if (popup._pendingNormalSubmit) {
        var normalContext = popup._pendingNormalSubmit;
        var mergedBonusLines = normalContext.fixedBonusLines.concat(pickedLines);

        try {
          var items = buildNormalProductItems(normalContext.form, normalContext.picker, mergedBonusLines);
          var cart = getCartRoot();

          await addCartItems(items, cart, normalContext.submitEl, 'normal');

          closeBonusPopupElement(popup);
        } catch (error) {
          console.error('Bonus add to cart error:', error);
          showBonusPopupError(popup, error.message || 'Unable to add to cart.');
        }
      } else {
        // Popup was opened without a pending flow (e.g. a standalone
        // "choose your gift" trigger with no form to submit) — nothing to do.
        closeBonusPopupElement(popup);
      }
    } finally {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
      if (spinner) spinner.classList.add('hidden');
    }
  });
}

/*
  Toggles a CSS class on the Add to Cart button the moment the shopper
  changes the product's variant (not just at click/submit time), so the
  button's whole look can change immediately to reflect whether THIS
  variant comes with a free gift — see custom.free_items and
  shouldOfferBonusForCurrentVariant() above.

  Deliberately just a class, not fixed text or markup: .pi-atc--free-gift
  can be styled in CSS however extensively is needed (different background,
  icon, border, size — a fully different-looking button), without this
  script needing to know or hardcode what that should look like. Runs once
  on page load too, so the button already matches whichever variant is
  selected by default before the shopper touches anything.
*/
function updateBonusButtonState(root, form) {
  var submitButton = form && form.querySelector('[type="submit"]');

  if (!submitButton) return;

  var hasBonus = root.querySelector('[data-bonus-popup]') || root.querySelector('[data-bonus-fixed]');
  var eligible = !!hasBonus && shouldOfferBonusForCurrentVariant(root, form);

  submitButton.classList.toggle('pi-atc--free-gift', eligible);
}

function findNormalProductForm(root) {
  return root.querySelector('form[action*="/cart/add"]:not(bundle-form form)') ||
    root.querySelector('product-form form');
}

function setupNormalProductBonusButtonState() {
  function refreshAllOnPage() {
    document.querySelectorAll('product-info, [id^="shopify-section-"]').forEach(function (root) {
      if (root.querySelector('bundle-form')) return;

      var hasBonus = root.querySelector('[data-bonus-popup]') || root.querySelector('[data-bonus-fixed]');

      if (!hasBonus) return;

      var form = findNormalProductForm(root);

      if (!form) return;

      updateBonusButtonState(root, form);
    });
  }

  // Live update on every variant change.
  document.addEventListener('change', function (event) {
    var root = event.target.closest('product-info') || event.target.closest('[id^="shopify-section-"]');

    if (!root) return;
    if (root.querySelector('bundle-form')) return;

    var hasBonus = root.querySelector('[data-bonus-popup]') || root.querySelector('[data-bonus-fixed]');

    if (!hasBonus) return;

    var form = findNormalProductForm(root);

    if (!form) return;

    updateBonusButtonState(root, form);
  });

  // Match whichever variant is selected by default on first paint.
  refreshAllOnPage();
}

/*
  Close/cancel button for the bonus popup. This used to be wired up inside
  BundleForm.connectedCallback() (BundleForm.bindBonusPopupCloseButtons()),
  which only ever runs when a <bundle-form> element is on the page — true
  for bundle products, but NOT for a normal (non-bundle) product with a
  bonus/free item, where the popup can open (via setupNormalProductBonus)
  but no listener was ever registered to close it. Standalone here so it
  works in both cases.
*/
function setupBonusPopupClose() {
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-bonus-popup-close], [data-bonus-popup-cancel]');

    if (!trigger) return;

    event.preventDefault();

    // Close the popup this button actually belongs to, not just "the first
    // [data-bonus-popup] on the page" — if the section is ever rendered
    // twice (see the duplicate customElements.define errors this theme has
    // shown), there could be two popups, and grabbing the first one would
    // silently close the wrong (already-hidden) one while the visible one
    // stayed open.
    var popup = trigger.closest('[data-bonus-popup]') || document.querySelector('[data-bonus-popup]');

    closeBonusPopupElement(popup);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    var popup = document.querySelector('[data-bonus-popup].is-open');

    if (!popup) return;

    closeBonusPopupElement(popup);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    rememberMainProduct(document.querySelector('bundle-selects'));
    setupNormalProductBonus();
    setupBonusPopupSubmit();
    setupBonusPopupClose();
    setupNormalProductBonusButtonState();
  });
} else {
  rememberMainProduct(document.querySelector('bundle-selects'));
  setupNormalProductBonus();
  setupBonusPopupSubmit();
  setupBonusPopupClose();
  setupNormalProductBonusButtonState();
}

})();